import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import type { ButtonHTMLAttributes, ComponentProps } from "react";

// 버튼 (DS Button). 화면 영역 하나에 primary는 하나, muse는 Muse 보내기에만 쓴다

export type ButtonVariant = "primary" | "secondary" | "danger" | "muse" | "ghost";
export type ButtonSize = "md" | "sm";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "st-btn--primary",
  secondary: "st-btn--secondary",
  danger: "st-btn--danger",
  muse: "st-btn--muse",
  // DS에 없는 글자만 있는 버튼: 표 안·메뉴 옆처럼 테두리가 시끄러운 곳
  ghost: "bg-transparent text-ink-muted hover:enabled:bg-surface-hover hover:enabled:text-ink",
};

/** DS 버튼 클래스. 링크를 버튼처럼 보일 때도 쓴다 */
export function buttonClass({
  variant = "primary",
  size = "md",
  iconOnly = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  className?: string;
} = {}): string {
  return [
    "st-btn",
    VARIANT_CLASS[variant],
    size === "sm" ? "st-btn--sm" : "",
    iconOnly ? "st-btn--icon" : "",
    className ?? "",
  ]
    .filter((part) => part !== "")
    .join(" ");
}

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 글자 앞 아이콘 (lucide, 16px) */
  icon?: LucideIcon;
  type?: "button" | "submit";
}

export function Button({
  variant = "primary",
  size = "md",
  icon: Icon,
  type = "button",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClass({ variant, size, className })} {...rest}>
      {Icon !== undefined && <Icon aria-hidden="true" />}
      {children}
    </button>
  );
}

interface LinkButtonProps extends ComponentProps<typeof Link> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
}

/** 다른 화면으로 가는 버튼 모양 링크 (예: "필드 목록 열기") */
export function LinkButton({
  variant = "secondary",
  size = "md",
  icon: Icon,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link
      className={buttonClass({ variant, size, className: `no-underline ${className ?? ""}` })}
      {...rest}
    >
      {Icon !== undefined && <Icon aria-hidden="true" />}
      {children}
    </Link>
  );
}
